import { type ReactNode } from "react";
import { Button, Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

export interface UnavailableUsersWarningPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  unavailableUsers: TellescopeModelFromName<"users">[];
  /** The element the warning anchors to (e.g. the submit button). */
  children: ReactNode;
}

export const UnavailableUsersWarningPopover = ({
  open,
  onOpenChange,
  onConfirm,
  unavailableUsers,
  children,
}: UnavailableUsersWarningPopoverProps) => {
  const { t } = useTranslation("calendar");

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent align="end" className="w-80 space-y-3" side="top">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-500" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {unavailableUsers.length > 1 ? t("Users Unavailable") : t("User Unavailable")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("At least one of the users selected isn't available during the selected time")}
            </p>
          </div>
        </div>
        <ul className="list-disc space-y-1 pl-8">
          {unavailableUsers.map((user) => (
            <li key={user.id} className="text-sm">
              {formatName(user) || t("User: {{id}}", { id: user.id })}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button
            size="sm"
            data-testid="unavailable-users-warning-confirm"
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {t("Continue anyway")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
